import { Transition } from '@headlessui/react';
import { Children, cloneElement, isValidElement, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { Icon, LogoOrIcon } from '~/components/granular';
import { contextFactory } from '~/utils/contextFactory';
import { classNames } from '~/utils/styles';

import type { PanelContextProps, PanelFooterProps, PanelHeaderProps, PanelProps } from './Panel.types';

export const [PanelContext, usePanelContext] = contextFactory<PanelContextProps>();

export function Panel({ children, collapseOnResize = true, collapsedWidth = 1000 }: PanelProps) {
  const { width } = useWindowSize();
  const shouldCollapse = collapseOnResize && width < collapsedWidth;
  const [isCollapsed, setIsCollapsed] = useState(shouldCollapse);

  // Collapse again whenever the window or the threshold changes, while still letting the user expand it manually
  const resizeKey = `${width}|${collapsedWidth}|${collapseOnResize}`;
  const [prevResizeKey, setPrevResizeKey] = useState(resizeKey);
  if (resizeKey !== prevResizeKey) {
    setPrevResizeKey(resizeKey);
    if (shouldCollapse) {
      setIsCollapsed(true);
    }
  }

  function handleToggleCollapse() {
    setIsCollapsed(!isCollapsed);
  }

  return (
    <PanelContext.Provider value={{ isCollapsed, onToggleCollapse: handleToggleCollapse }}>
      <div
        className={classNames(
          'flex h-screen flex-col border-r border-neutral-300 bg-neutral-100 transition-width duration-300 ease-in-out',
          {
            'w-[56px]': isCollapsed,
            'w-[200px]': !isCollapsed,
          },
        )}
        role="complementary"
      >
        {Children.map(children, (child) => {
          if (isValidElement<{ isCollapsed: boolean }>(child)) {
            return cloneElement(child, { isCollapsed });
          }
          return child;
        })}
      </div>
    </PanelContext.Provider>
  );
}

export function PanelHeader({ title, subtitle, logo }: PanelHeaderProps) {
  const { isCollapsed } = usePanelContext();

  return (
    <div className={classNames('flex items-center gap-2 text-neutral-1000', isCollapsed ? 'p-3' : 'p-4')}>
      <div>
        <LogoOrIcon isCollapsed={isCollapsed} logo={logo} />
      </div>

      <Transition
        as="div"
        className="overflow-hidden whitespace-nowrap"
        enter="transform transition-opacity duration-300"
        enterFrom="opacity-0 "
        enterTo="opacity-100"
        leave="transform duration-0 transition-opacity ease-in-out"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        show={!isCollapsed}
      >
        <div className="overflow-hidden text-base leading-5 font-semibold text-ellipsis">{title}</div>
        <div className="overflow-hidden text-3xs leading-3 font-normal text-ellipsis">{subtitle}</div>
      </Transition>
    </div>
  );
}

export function PanelBody({ children }: PanelFooterProps) {
  return <div className="overflow-hidden px-3 pt-8">{children}</div>;
}

export function PanelFooter({ children }: PanelFooterProps) {
  const { isCollapsed, onToggleCollapse } = usePanelContext();
  return (
    <div className="flex grow-0 p-4">
      <Transition
        as="div"
        className="overflow-hidden whitespace-nowrap"
        enter="transform transition-opacity duration-300"
        enterFrom="opacity-0 "
        enterTo="opacity-100"
        leave="transform duration-0 transition-opacity ease-in-out"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        show={!isCollapsed}
      >
        {children}
      </Transition>
      <div className="ml-auto">
        <button
          className="flex h-6 w-6 items-center justify-center border border-neutral-300 bg-white text-neutral-700 hover:shadow-sm"
          data-state={isCollapsed ? 'closed' : 'open'}
          onClick={onToggleCollapse}
          type="button"
        >
          <Icon height={16} icon={isCollapsed ? 'MenuUnfoldIcon' : 'MenuFoldIcon'} width={16} />
        </button>
      </div>
    </div>
  );
}
